/**
 * State setters for the User Journey page.
 *
 * The page predates this file and sets state inline; only the two payroll
 * read slices (connection summary, sync history), each with the generation
 * token that keeps an overlapping read from landing last, the sync card's
 * expanded rows and the header's payroll sources popover live here. Mirrors the idiom in `reports.delegate.js`: plain
 * class, no state of its own, object-spread copies through `this.setState`.
 */
class UserDelegate {
    /**
     * Replaces the connection summary slice.
     * @param {{ loading?: boolean, error?: Object | null, data?: Array<Object> | null }} patch
     */
    setPayrollConnections(patch) {
        const { payrollConnections } = this.state;
        this.setState({ payrollConnections: { ...(payrollConnections || {}), ...patch } });
    }

    /** Replaces the sync-history slice (KAN-679): loading, error, data, timezone, visible. */
    setSyncHistory(patch) {
        const { syncHistory } = this.state;
        this.setState({ syncHistory: { ...(syncHistory || {}), ...patch } });
    }

    /**
     * Flips one dimension row of the Payroll Sync Status card between
     * expanded and collapsed; the other rows keep their state.
     * @param {string} key Sync dimension key (`identity`, `paystubs`, …)
     */
    toggleSyncDimension(key) {
        this.setState((previous) => ({
            openSyncDimensions: {
                ...(previous.openSyncDimensions || {}),
                [key]: !previous.openSyncDimensions?.[key],
            },
        }));
    }

    /**
     * Opens or closes the header's payroll sources popover.
     * @param {boolean} open
     */
    setPayrollSourcesOpen(open) {
        this.setState({ payrollSourcesOpen: Boolean(open) });
    }

    /**
     * Generation token for the sync-history read (Retry and a post-action
     * refresh can overlap). Outside React state: read inside a promise
     * continuation, where a batched setState may not have flushed.
     */
    bumpSyncHistoryGeneration() {
        this._syncHistoryGeneration = (this._syncHistoryGeneration || 0) + 1;
        return this._syncHistoryGeneration;
    }

    isCurrentSyncHistoryGeneration(generation) {
        return (this._syncHistoryGeneration || 0) === generation;
    }

    /**
     * Same token scheme for the connection summary: an admin action's refresh
     * and a slow initial read can both be in flight, and the slower one must
     * not overwrite the newer answer.
     * @returns {number}
     */
    bumpPayrollConnectionsGeneration() {
        this._payrollConnectionsGeneration = (this._payrollConnectionsGeneration || 0) + 1;
        return this._payrollConnectionsGeneration;
    }

    /**
     * @param {number} generation
     * @returns {boolean}
     */
    isCurrentPayrollConnectionsGeneration(generation) {
        return (this._payrollConnectionsGeneration || 0) === generation;
    }
}

export default UserDelegate;
